import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { CalendarModule } from 'primeng/calendar';
import { UserService } from '../../../../services/user.service';
import { AccordionComponent } from '../../../../shared/components/accordion/accordion.component';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { TimesheetUnitsHandler } from '../../timesheets/timesheets-add-entries/timesheet-units.handler';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ClaimsService } from '../../../../services/claims.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { NzInputModule } from 'ng-zorro-antd/input';
@Component({
  selector: 'ppl-auth-utilization-analysis',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzSelectModule,
    ButtonComponent,
    CalendarModule,
    AccordionComponent,
    ListTableComponent,
    NzDatePickerModule,
    NzInputModule
  ],
  providers: [TimesheetUnitsHandler],
  templateUrl: './auth-utilization-analysis.component.html',
  styleUrl: './auth-utilization-analysis.component.css',
})
export class AuthUtilizationAnalysisComponent implements OnInit {
  authAnalysisForm!: FormGroup;
  programName: any;
  payPeriod: any;
  showAdditionalFields = false;
  dates: any;
  showResults = false;
  analyzeResults: any[] = [];
  analyzeResultList: any[] = [];
  customTimeError: boolean = false;
  formSubmitted: boolean = false;
  analyseCols = [
    {
      field: 'codeDesc',
      header: 'Service Code',
      jsonpath: 'codeDesc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'orgName',
      header: 'Org Name',
      jsonpath: 'orgName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalAvailableUnits',
      header: 'Total Available Units',
      jsonpath: 'totalAvailableUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'usedUnits',
      header: 'Used Units',
      jsonpath: 'usedUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'remainingUnits',
      header: 'Remaining Units',
      jsonpath: 'remainingUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'statusMessage',
      header: 'Message',
      jsonpath: 'statusMessage',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'canSubmitTimesheet',
      header: 'Status',
      jsonpath: 'canSubmitTimesheet',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  minutes = [
    '00',
    ...Array.from({ length: 59 }, (_, i) =>
      (i + 1).toString().padStart(2, '0')
    ),
  ];
  cols = [
    {
      field: 'tasTimesheetlineDetailId',
      header: 'Timesheet ID',
      jsonpath: 'tasTimesheetlineDetailId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'PA Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerPplId',
      header: 'PA PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceFromDate',
      header: 'From Date',
      jsonpath: 'serviceFromDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'usedUnits',
      header: 'Utilized Units',
      jsonpath: 'usedUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private claimsService: ClaimsService,
    private dateTimeFormatterService: DateTimeFormatterService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.authAnalysisForm = this.fb.group({
      participantPplId: [null, Validators.required],
      dateOfService: [null, Validators.required],
      hours: [null],
      minutes: [null],
      units: [{ value: null, disabled: true }],
    });

    this.authAnalysisForm.get('hours')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits();
    });
    this.authAnalysisForm.get('minutes')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits();
    });
  }

  updateTimesheetUnits(): void {
    const durationHours = this.authAnalysisForm.get('hours')?.value;
    const durationMins = this.authAnalysisForm.get('minutes')?.value;
    const totalMinutes = Number(durationHours) * 60 + Number(durationMins);
    const units = Math.ceil(totalMinutes / 15);
    this.authAnalysisForm.get('units')?.setValue(units);
  }

  onCancel() {
    this.authAnalysisForm.reset();
    this.analyzeResults = [];
    this.analyzeResultList = [];
    this.formSubmitted = false;
    this.customTimeError = false;
  }

  onConsumerChange(event: any) {
    this.authAnalysisForm.patchValue({
      dateOfService: null,
      hours: null,
      minutes: null,
      units: null,
    });
    this.analyzeResults = [];
    this.analyzeResultList = [];
    this.showResults = false;
  }

  analyzeUtilization() {
    this.formSubmitted = true;

    const hours = this.authAnalysisForm.get('hours')?.value;
    const minutes = this.authAnalysisForm.get('minutes')?.value;
    const units = this.authAnalysisForm.get('units')?.value;

    const isTimeEmpty = (!hours && !minutes);
    const isUnitsZero = (units === 0 || units === '0');
  
    if (isTimeEmpty || isUnitsZero) {
      this.customTimeError = true;
      return;
    } else {
      this.customTimeError = false;
    }
    if (this.authAnalysisForm.invalid) {
      return;
    }

    const formData = this.authAnalysisForm.getRawValue();
    this.getAnalysis(formData);
    this.getAnalysisTimesheets(formData);
  }

  getAnalysis(formData: any) {
    const payload = {
      dateOfService: this.dateTimeFormatterService.formatDateToString(
        formData.dateOfService
      ),
      units: formData.units,
      participantId: formData.participantPplId,
    };

    this.claimsService
      .getAuthUtilizationAnalysis(payload, this.programName)
      .subscribe((data: any) => {
        this.analyzeResultList = data.responsedata;
        this.showResults = true;
      });
  }

  getAnalysisTimesheets(formData: any) {
    const payload = {
      dateOfService: this.dateTimeFormatterService.formatDateToString(
        formData.dateOfService
      ),
      programCode: this.programName,
      participantPplId: formData.participantPplId,
    };
    this.claimsService
      .getAuthUtilizationAnalysisTS(payload, this.programName)
      .subscribe((data: any) => {
        this.analyzeResults = data.responsedata;
        this.showResults = true;
      });
  }

  onPageLoad(event: any) {
    // this.searchClaims(
    //   this.appliedFilters,
    //   event.startingPageNumber,
    //   event?.first ?? 0
    // );
  }

  rowClicked(event: any) {
    console.log('row clicked', event);
  }
}
